import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useSpuPdfParam from "../../../stores/spuPdfParam";

interface ParameterSheetWorkbenchProps {
  psSeries?: string;
  psModel?: string;
}

const ParameterSheetWorkbench = ({
  psSeries,
  psModel,
}: ParameterSheetWorkbenchProps) => {
  const [searchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const generatedRef = useRef<any>(null);
  const handledRequestsRef = useRef<Set<string>>(new Set());
  const uploadedCurveKeysRef = useRef<Set<string>>(new Set());
  const uploadingRef = useRef(false);
  const performanceCurveImage = useSpuPdfParam((state) => state.images.performance);
  const series = psSeries || searchParams.get("psSeries") || "";
  const model = psModel || searchParams.get("psModel") || "";

  const iframeSrc = useMemo(() => {
    if (!series || !model) return "/parameter-sheet/ui";
    const target = new URLSearchParams({
      series,
      series_name: series,
      model_key: model,
    });
    return `/parameter-sheet/ui?${target.toString()}`;
  }, [model, series]);

  const normalizeModel = (value: string) =>
    String(value || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "");

  const isSameRequestedModel = useCallback((generatedModel: string) => {
    const expected = normalizeModel(model);
    const actual = normalizeModel(generatedModel);
    if (!expected || !actual) return false;
    return expected === actual || expected.endsWith(actual) || actual.endsWith(expected);
  }, [model]);

  const uploadCurveImage = useCallback(async () => {
    const generated = generatedRef.current;
    if (
      uploadingRef.current ||
      !series ||
      !model ||
      !generated?.requestId ||
      !performanceCurveImage ||
      !String(performanceCurveImage).startsWith("data:image/")
    ) {
      return;
    }
    if (String(generated.seriesName || "").toUpperCase() !== String(series).toUpperCase()) return;
    if (!isSameRequestedModel(String(generated.modelKey || ""))) return;

    const curveKey = [
      String(series).toUpperCase(),
      normalizeModel(model),
      String(performanceCurveImage).slice(0, 120),
    ].join("::");
    if (uploadedCurveKeysRef.current.has(curveKey)) return;
    uploadedCurveKeysRef.current.add(curveKey);
    uploadingRef.current = true;

    try {
      const blob = await fetch(performanceCurveImage).then((response) => response.blob());
      const form = new FormData();
      form.append("source_request_id", generated.requestId);
      form.append("request_id", crypto.randomUUID());
      form.append("role", "curve");
      form.append("file", blob, `${series}_${model}_performance_curve.png`);

      const response = await fetch("/parameter-sheet/replace-visual", {
        method: "POST",
        body: form,
      });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || "replace visual failed");
      }
      iframeRef.current?.contentWindow?.postMessage(
        {
          source: "xzp_spu_detail",
          type: "parameter-sheet-visual-replaced",
          response: data,
        },
        window.location.origin
      );
    } catch (error) {
      console.warn("parameter sheet curve injection failed", error);
    } finally {
      uploadingRef.current = false;
    }
  }, [isSameRequestedModel, model, performanceCurveImage, series]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.source !== "selection_ai_parameter_sheet") return;
      if (data.type !== "parameter-sheet-generated") return;
      if (!data.requestId || handledRequestsRef.current.has(data.requestId)) return;
      handledRequestsRef.current.add(data.requestId);
      generatedRef.current = data;
      uploadCurveImage();
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [uploadCurveImage]);

  useEffect(() => {
    uploadCurveImage();
  }, [performanceCurveImage, uploadCurveImage]);

  return (
    <div style={{ width: "100%", minHeight: 1040 }}>
      <iframe
        ref={iframeRef}
        title="技术参数表工作台"
        src={iframeSrc}
        style={{
          display: "block",
          width: "100%",
          minHeight: 1040,
          border: 0,
          background: "transparent",
        }}
      />
    </div>
  );
};

export default ParameterSheetWorkbench;
